import { FC, Fragment, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { logEvent } from 'features/Analytics/analytics';
import { getDisputePath } from 'features/Bank/routes';
import { formatCalendar } from 'helpers/date';
import { capitalizeFirstLetter } from 'helpers/string';
import * as ContentLayout from 'layouts/ContentLayout';

import DisputableTransactionRow from '../../components/DisputableTransactionRow';
import StickyFooter from '../../components/StickyFooter';
import { useDisputableTransactions } from '../../hooks/useDisputableTransactions';
import { useDisputableTransactionsByDay } from '../../hooks/useDisputableTransactionsByDay';
import { useToggleSelectedTransactions } from '../../hooks/useToggleSelectedTransactions';
import locales from './locales';

const DisputeTransactions: FC = () => {
  const { search } = useLocation();
  const transactionIds = useMemo(
    () => new URLSearchParams(search).getAll('transactionId'),
    [search],
  );

  const { selectedTransactionIds, toggle } =
    useToggleSelectedTransactions(transactionIds);

  const {
    disputableTransactions,
    error,
    hasNextPage,
    isLoading,
    isLoadingMore,
    loadMore,
  } = useDisputableTransactions();

  const disputableTransactionsPerDay = useDisputableTransactionsByDay(
    disputableTransactions,
  );

  if (error) {
    return <ErrorView />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <ContentLayout.Root>
      <ContentLayout.Body paddingBottom="0" size="sm">
        <BackButton marginBottom={32} />
        <ContentLayout.Header
          description={locales.subtitle}
          title={locales.title}
        />

        <Box mt="space-12">
          {disputableTransactionsPerDay?.map((section) => (
            <Fragment key={section.title}>
              <Text fontWeight="weight-500" paddingX="space-16" variant="light">
                {capitalizeFirstLetter(formatCalendar(new Date(section.date)))}
              </Text>
              <Box mb="space-16">
                {section.data.map((transaction) => (
                  <DisputableTransactionRow
                    isBoxed={false}
                    isSelected={selectedTransactionIds.includes(
                      transaction.transactionId,
                    )}
                    key={transaction.transactionId}
                    onChange={toggle}
                    transaction={transaction}
                  />
                ))}
              </Box>
            </Fragment>
          ))}
        </Box>

        {hasNextPage ? (
          <Button
            alignSelf="center"
            isLoading={isLoadingMore}
            marginY="space-32"
            onClick={loadMore}
            variant="secondary"
          >
            {locales.seeMore}
          </Button>
        ) : null}

        <StickyFooter>
          <Button
            as={Link}
            isDisabled={selectedTransactionIds.length < 1}
            onClick={(e) => {
              if (selectedTransactionIds.length < 1) {
                e.preventDefault();
              } else {
                logEvent({ name: 'Submit_Dispute_Transaction_Multiple' });
              }
            }}
            to={getDisputePath(selectedTransactionIds)}
          >
            {locales.formatString(
              selectedTransactionIds.length > 1
                ? locales.submitOperations
                : locales.submitOperation,
              {
                count: selectedTransactionIds.length,
              },
            )}
          </Button>
        </StickyFooter>
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default DisputeTransactions;
